import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type { ApiResponse, BvAttachment, BvAttachmentCategory } from '../types/billValidationTypes';

const BUCKET = 'bv-attachments';

async function getOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('base_organisation_members')
    .select('organisation_id')
    .eq('user_id', user.id)
    .single();
  return data?.organisation_id ?? null;
}

// Storage path: {org_id}/{entity_type}/{entity_id}/{filename}
function storagePath(orgId: string, entityType: 'sites' | 'orgs', entityId: string, fileName: string): string {
  const uid = crypto.randomUUID();
  const safe = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  return `${orgId}/${entityType}/${entityId}/${uid}_${safe}`;
}

export const attachmentService = {
  list: async (filters: { orgId?: string; siteId?: string }): Promise<ApiResponse<BvAttachment[]>> => {
    try {
      const orgId = filters.orgId ?? (await getOrgId());
      if (!orgId) return { success: false, error: 'Organization not found' };

      let query = supabase
        .from('bv_attachment')
        .select('*')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });

      if (filters.siteId) query = query.eq('site_id', filters.siteId);

      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data ?? [] };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  upload: async (
    file: File,
    options: {
      siteId?: string;
      category: BvAttachmentCategory;
      loaSignedDate?: string;
      notes?: string;
    },
  ): Promise<ApiResponse<BvAttachment>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      const entityType = options.siteId ? 'sites' : 'orgs';
      const entityId = options.siteId ?? orgId;
      const path = storagePath(orgId, entityType, entityId, file.name);

      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { upsert: false });
      if (uploadError) throw uploadError;

      const { data, error: dbError } = await supabase
        .from('bv_attachment')
        .insert({
          org_id: orgId,
          site_id: options.siteId ?? null,
          file_name: file.name,
          file_type: file.type,
          file_size_bytes: file.size,
          category: options.category,
          storage_path: path,
          loa_signed_date: options.loaSignedDate ?? null,
          notes: options.notes ?? null,
        })
        .select()
        .single();
      if (dbError) throw dbError;

      return { success: true, data };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  getSignedUrl: async (storagePath: string, expiresIn = 3600): Promise<string | null> => {
    const { data } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(storagePath, expiresIn);
    return data?.signedUrl ?? null;
  },

  download: async (storagePath: string): Promise<Blob | null> => {
    const { data, error } = await supabase.storage.from(BUCKET).download(storagePath);
    if (error || !data) return null;
    return data;
  },

  delete: async (id: string, filePath: string): Promise<ApiResponse<null>> => {
    try {
      // Delete DB row first — if storage fails, DB row is gone (no phantom UI)
      // If DB fails, file is still accessible and can be retried
      const { error: dbError } = await supabase.from('bv_attachment').delete().eq('id', id);
      if (dbError) throw dbError;

      const { error: storageError } = await supabase.storage.from(BUCKET).remove([filePath]);
      if (storageError) {
        // Log but don't surface — orphan file is better than stuck UI
        console.error('Storage delete failed after DB delete:', storageError.message);
      }

      return { success: true, data: null };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },
};
